import { nodesFlux } from '@candulabs/craft';
import { Alert, styled } from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import React, { Fragment, ReactElement, useMemo } from 'react';

import { CollectionContent } from '@candu-ai/collections/types';
import DashedOutline from 'craft/components/DashedOutline';
import { CollectionContext } from 'craft/contexts';
import { useEditorSelector, useNodeContext } from 'craft/hooks';

import { CollectionMetadataLoader } from './CollectionMetadataLoader';

const NoRecordsWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  width: 100%;

  & > div {
    min-height: auto;
  }
`;

const AlertWrapper = styled.div`
  left: 50%;
  max-width: 100%;
  overflow: hidden;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface Props {
  collectionId: string;
  children?: ReactElement | ReactElement[];
  resource?: CollectionContent;
}

export const CollectionChildrenImpl = ({ collectionId, children, resource }: Props) => {
  const { id: contextId } = useNodeContext();

  const propsSelector = useMemo(() => nodesFlux.selectors.getNodeProps(contextId), [contextId]);
  const { maxRecords } = useEditorSelector(propsSelector) ?? {};

  if (!resource || !resource?.records?.length) {
    return (
      <NoRecordsWrapper>
        <CollectionMetadataLoader collectionId={collectionId} />
        <DashedOutline />
        <DashedOutline />
        <DashedOutline />
        <DashedOutline />
        <AlertWrapper>
          <Alert
            variant="dark"
            intent="danger"
            message="This Collection does not have any records to display"
          />
        </AlertWrapper>
      </NoRecordsWrapper>
    );
  }

  return (
    <Fragment>
      <CollectionMetadataLoader collectionId={collectionId} />
      {resource.records.slice(0, maxRecords).map(({ id, record }: any, index: number) => (
        <CollectionContext.Provider
          key={id}
          value={{ id, record, collectionId: resource.id, index }}
        >
          {children}
        </CollectionContext.Provider>
      ))}
    </Fragment>
  );
};

export const CollectionChildren = connectWithHammock(({ collectionId }) => ({
  endpoint: ENDPOINTS.collectionContent,
  query: { finder: 'withSorting', inAscendingOrder: 'true', id: collectionId },
}))(CollectionChildrenImpl);
